import type { Theme } from '@mui/material';

const splitGradient = (left: string, right: string) =>
  `linear-gradient(45deg, ${left} 0%, ${left} 50%, ${right} 50%, ${right} 100%)`;

export const neobetRaisedActionButtonStyles = {
  button: {
    position: 'relative',
    minWidth: 108,
    height: 40,
    px: 1.75,
    py: 0.75,
    border: 0,
    borderRadius: 3,
    color: 'neutral.25',
    backgroundColor: 'primary.main',
    backgroundImage: (t: Theme) => splitGradient(t.palette.primary[400], t.palette.primary[600]),
    backgroundPosition: '80% 0',
    backgroundSize: '200% 100%',
    boxShadow: (t: Theme) => `0 -3px 0 0 ${t.palette.primary[700]} inset, 0 2px 0 0 ${t.palette.primary[200]} inset`,
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: '16px',
    overflow: 'hidden',
    textTransform: 'none',
    transition: 'background-position 0.3s ease-in-out, transform 120ms ease, box-shadow 180ms ease',
    '&::before': {
      content: '" "',
      position: 'absolute',
      top: 0,
      left: 10,
      width: 5,
      height: 2,
      backgroundColor: 'neutral.25',
      borderRadius: 16,
    },
    '&:hover': {
      backgroundPosition: '0 0',
      boxShadow: (t: Theme) => `0 -3px 0 0 ${t.palette.primary[700]} inset, 0 2px 0 0 ${t.palette.primary[200]} inset`,
    },
    '&:active': {
      transform: 'translateY(1px)',
      boxShadow: (t: Theme) => `0 -1px 0 0 ${t.palette.primary[700]} inset, 0 1px 0 0 ${t.palette.primary[200]} inset`,
    },
    '&.Mui-disabled': {
      color: 'neutral.200',
      backgroundColor: 'neutral.600',
      backgroundImage: 'none',
      boxShadow: 'none',
      opacity: 1,
      transform: 'none',
      '&::before': {
        display: 'none',
      },
    },
  },
  withBorder: {
    border: '3px solid',
    borderColor: 'neutral.800',
  },
  label: {
    position: 'relative',
    display: 'inline-grid',
    alignItems: 'center',
    justifyItems: 'center',
    maxWidth: 1,
    p: 0.25,
    whiteSpace: 'nowrap',
    typography: 'h5',
    fontFamily: (t: Theme) => t.typography.h5.fontFamily ?? 'inherit',
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: 1.5,
  },
  stroke: {
    gridArea: '1 / 1',
    color: 'neutral.25',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: 1.5,
    WebkitTextStroke: (t: Theme) => `2px ${t.palette.primary[700]}`,
    '.Mui-disabled &': {
      color: 'neutral.200',
      WebkitTextStroke: 0,
    },
  },
  lowerStroke: {
    transform: 'translateY(var(--neobet-raised-label-shadow-offset, 1px))',
    '.Mui-disabled &': {
      display: 'none',
    },
  },
  text: {
    gridArea: '1 / 1',
    position: 'relative',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: 1.5,
  },
  variants: {
    authForm: {
      height: 56,
      borderRadius: 3,
      '& > .MuiTypography-root': {
        typography: 'body2',
        fontFamily: (t: Theme) => t.typography.h5.fontFamily ?? 'inherit',
      },
    },
    form: {
      width: 1,
      height: 48,
      borderRadius: 2,
    },
    header: {
      '--neobet-raised-label-shadow-offset': '1px',
      minWidth: { xs: 74, md: 108 },
      height: { xs: 32, md: 40 },
      px: { xs: 1.25, md: 2 },
      borderRadius: 2.5,
      lineHeight: { xs: 1, md: 'normal' },
      '& > .MuiTypography-root': {
        height: 1,
        display: 'inline-grid',
        alignItems: 'center',
        typography: 'body4',
        fontFamily: (t: Theme) => t.typography.h5.fontFamily ?? 'inherit',
        fontSize: { xs: '11px', md: '14px' },
        lineHeight: 1.5,
        p: 0,
      },
      '& > .MuiTypography-root .MuiTypography-root': {
        fontSize: { xs: '11px', md: '14px' },
        lineHeight: 1.5,
      },
    },
    icon: {
      '--neobet-raised-label-shadow-offset': '1px',
      minWidth: 0,
      width: 34,
      height: 34,
      p: 0,
      flexShrink: 0,
      borderRadius: 999,
      '& > .MuiTypography-root': {
        typography: 'h3',
        p: 0,
        lineHeight: 1,
      },
      '& > .MuiTypography-root .MuiTypography-root': {
        lineHeight: 1,
      },
    },
  },
};
